import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { renderUserAvatar } from "@/lib/avatar";
import { useAuth } from "@/lib/auth.jsx";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Filter } from "lucide-react";
import { toast } from "sonner";
import { useDrawer } from "@/components/RightDrawer";

const STATUSES = [
  { id: "backlog", label: "Backlog" },
  { id: "todo", label: "To Do" },
  { id: "in_progress", label: "In Progress" },
  { id: "blocked", label: "Blocked" },
  { id: "review", label: "Review" },
  { id: "done", label: "Done" },
];
const PRIORITY_COLORS = { low: "bg-zinc-100 text-zinc-700", medium: "bg-blue-100 text-blue-700", high: "bg-orange-100 text-orange-700", urgent: "bg-red-100 text-red-700" };

function formatDueDate(iso) {
  if (!iso) return "";
  try {
    // Accept ISO date (YYYY-MM-DD) or full datetime.
    const d = new Date(iso.length <= 10 ? `${iso}T00:00:00` : iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

export default function TasksView({ subscribe }) {
  const { user } = useAuth();
  const { openDrawer } = useDrawer();
  const [tasks, setTasks] = useState([]);
  const [members, setMembers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [view, setView] = useState("kanban");
  const [filter, setFilter] = useState("all");

  const load = async () => {
    const [t, m, p] = await Promise.all([
      api.get("/tasks"),
      api.get("/orgs/current/members"),
      api.get("/projects").catch(() => ({ data: [] })),
    ]);
    setTasks(t.data);
    setMembers(m.data);
    setProjects(p.data);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!subscribe) return;
    return subscribe((type) => {
      if (type.startsWith("task.")) load();
    });
  }, [subscribe]);

  const filtered = useMemo(() => {
    let arr = tasks;
    if (filter === "mine") arr = arr.filter((t) => (t.assignee_ids || []).includes(user?.id));
    if (filter === "overdue") arr = arr.filter((t) => t.due_date && new Date(t.due_date) < new Date() && t.status !== "done");
    return arr;
  }, [tasks, filter, user]);

  const byStatus = useMemo(() => {
    const map = {};
    for (const st of STATUSES) map[st.id] = [];
    for (const t of filtered) (map[t.status] || (map[t.status] = [])).push(t);
    return map;
  }, [filtered]);

  const openDetail = (t) => openDrawer("Task", t.id, <TaskDetail id={t.id} members={members} onChange={load}/>);

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-white" data-testid="tasks-view">
      <div className="h-14 border-b flex items-center justify-between px-6 shrink-0">
        <h1 className="text-lg font-semibold">Tasks</h1>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40 h-9 text-sm" data-testid="task-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All tasks</SelectItem>
              <SelectItem value="mine">My tasks</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
            </SelectContent>
          </Select>
          <Tabs value={view} onValueChange={setView}>
            <TabsList>
              <TabsTrigger value="kanban" data-testid="tasks-tab-kanban">Kanban</TabsTrigger>
              <TabsTrigger value="list" data-testid="tasks-tab-list">List</TabsTrigger>
            </TabsList>
          </Tabs>
          <NewTaskDialog members={members} projects={projects} onCreated={load}/>
        </div>
      </div>
      <div className="flex-1 overflow-auto scroll-thin p-6">
        {filtered.length === 0 ? (
          <EmptyTasksState filter={filter} />
        ) : view === "kanban" ? (
          <div className="grid grid-cols-6 gap-4 min-w-[1200px]">
            {STATUSES.map((s) => (
              <div key={s.id} className="bg-zinc-50 rounded-lg p-2 min-h-40" data-testid={`task-col-${s.id}`}>
                <div className="text-xs uppercase tracking-widest text-zinc-500 px-1 pb-2 flex justify-between">
                  <span>{s.label}</span>
                  <span>{(byStatus[s.id] || []).length}</span>
                </div>
                <div className="space-y-2">
                  {(byStatus[s.id] || []).map((t) => (
                    <TaskCard key={t.id} task={t} onClick={() => openDetail(t)}/>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-widest text-zinc-500 border-b"><tr>
              <th className="px-2 py-2">Title</th><th>Status</th><th>Priority</th><th>Assignees</th><th>Due</th>
            </tr></thead>
            <tbody>
              {filtered.map((t) => (
                <tr key={t.id} onClick={() => openDetail(t)} className="border-b hover:bg-zinc-50 cursor-pointer" data-testid={`task-row-${t.id}`}>
                  <td className="px-2 py-2 font-medium">{t.title}</td>
                  <td>{STATUSES.find((s) => s.id === t.status)?.label}</td>
                  <td><span className={`text-xs rounded-full px-2 py-0.5 ${PRIORITY_COLORS[t.priority] || ""}`}>{t.priority}</span></td>
                  <td>
                    <div className="flex -space-x-2">
                      {(t.assignees || []).map((a) => a && <img key={a.id} src={renderUserAvatar(a)} className="w-6 h-6 rounded-full border-2 border-white object-cover" alt="" title={a.name}/>)}
                    </div>
                  </td>
                  <td className="text-zinc-500">{t.due_date ? `Due ${formatDueDate(t.due_date)}` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function EmptyTasksState({ filter }) {
  const copy = filter === "mine"
    ? { title: "No tasks assigned to you yet", body: "When someone assigns you a task, it will show up here." }
    : filter === "overdue"
    ? { title: "Nothing overdue", body: "You're all caught up. Great job." }
    : { title: "No tasks yet", body: "Create your first task to start organizing work." };
  return (
    <div className="h-full flex flex-col items-center justify-center py-16" data-testid="tasks-empty-state">
      <div className="w-14 h-14 rounded-full bg-zinc-100 border border-zinc-200 flex items-center justify-center mb-4">
        <ListTodoIcon />
      </div>
      <div className="text-base font-semibold text-zinc-900">{copy.title}</div>
      <div className="text-sm text-zinc-500 mt-1 max-w-sm text-center">{copy.body}</div>
    </div>
  );
}

function ListTodoIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"
      className="text-zinc-500" aria-hidden>
      <rect x="3" y="5" width="6" height="6" rx="1"/>
      <path d="m3 17 2 2 4-4"/>
      <path d="M13 6h8"/>
      <path d="M13 12h8"/>
      <path d="M13 18h8"/>
    </svg>
  );
}

function TaskCard({ task, onClick }) {
  return (
    <div onClick={onClick} className="bg-white border border-zinc-200 rounded-md p-3 hover:border-zinc-400 cursor-pointer transition" data-testid={`task-card-${task.id}`}>
      <div className="text-sm font-medium mb-1">{task.title}</div>
      {task.description && <div className="text-xs text-zinc-500 line-clamp-2 mb-2">{task.description}</div>}
      <div className="flex items-center justify-between">
        <span className={`text-[10px] rounded-full px-1.5 py-0.5 ${PRIORITY_COLORS[task.priority] || ""}`}>{task.priority}</span>
        <div className="flex -space-x-1">
          {(task.assignees || []).slice(0, 3).map((a) => a && <img key={a.id} src={renderUserAvatar(a)} className="w-5 h-5 rounded-full border-2 border-white object-cover" alt="" title={a.name}/>)}
        </div>
      </div>
      {task.due_date && <div className="text-[10px] text-zinc-400 mt-1 mono" data-testid={`task-card-due-${task.id}`}>Due {formatDueDate(task.due_date)}</div>}
    </div>
  );
}

function NewTaskDialog({ members, projects, onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", description: "", priority: "medium", status: "todo", assignee_ids: [], due_date: "", project_id: "" });
  const create = async () => {
    try {
      const payload = { ...f };
      if (!payload.project_id) delete payload.project_id;
      await api.post("/tasks", payload);
      setOpen(false); toast.success("Task created"); onCreated();
      setF({ title: "", description: "", priority: "medium", status: "todo", assignee_ids: [], due_date: "", project_id: "" });
    } catch { toast.error("Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" className="bg-[var(--brand-accent)] text-white" data-testid="new-task-btn"><Plus className="w-4 h-4 mr-1"/> New Task</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Create task</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="task-title"/>
          <Textarea placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })}/>
          <div className="grid grid-cols-2 gap-2">
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent>{STATUSES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent></Select>
            <Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent>{["low", "medium", "high", "urgent"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><label className="text-xs text-zinc-500">Assign to</label>
            <div className="flex flex-wrap gap-1 mt-1 max-h-32 overflow-auto">
              {members.map((m) => (
                <button key={m.user_id} onClick={() => setF({ ...f, assignee_ids: f.assignee_ids.includes(m.user_id) ? f.assignee_ids.filter((x) => x !== m.user_id) : [...f.assignee_ids, m.user_id] })}
                  className={`text-xs rounded-full px-2 py-1 border ${f.assignee_ids.includes(m.user_id) ? "border-[var(--brand-accent)] bg-orange-50" : "border-zinc-200"}`}>
                  {m.user?.name}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs text-zinc-500" htmlFor="task-due-input">Due date (optional)</label>
            <Input id="task-due-input" type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="task-due-input"/>
            <div className="text-[11px] text-zinc-500 mt-1">When should this task be completed?</div>
          </div>
          {projects && projects.length > 0 && (
            <div>
              <label className="text-xs text-zinc-500">Project (optional)</label>
              <Select value={f.project_id || "__none__"} onValueChange={(v) => setF({ ...f, project_id: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="task-project"><SelectValue placeholder="No project"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No project</SelectItem>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button className="w-full bg-[var(--brand-primary)] text-white" onClick={create} data-testid="task-create-submit">Create</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TaskDetail({ id, members, onChange }) {
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  useEffect(() => { api.get(`/tasks/${id}`).then((r) => setData(r.data)); }, [id]);
  const patch = async (p) => {
    await api.patch(`/tasks/${id}`, p);
    const r = await api.get(`/tasks/${id}`);
    setData(r.data);
    onChange && onChange();
  };
  const addComment = async () => {
    if (!text.trim()) return;
    await api.post(`/tasks/${id}/comments`, { text });
    const r = await api.get(`/tasks/${id}`);
    setData(r.data);
    setText("");
  };
  if (!data) return <div className="p-4 text-sm text-zinc-500">Loading…</div>;
  const t = data.task;
  return (
    <div className="p-4 space-y-4">
      <div>
        <Input value={t.title} onChange={(e) => setData({ ...data, task: { ...t, title: e.target.value } })} onBlur={(e) => patch({ title: e.target.value })} className="text-lg font-semibold border-0 px-0"/>
      </div>
      <Textarea value={t.description || ""} onChange={(e) => setData({ ...data, task: { ...t, description: e.target.value } })} onBlur={(e) => patch({ description: e.target.value })}/>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs uppercase text-zinc-500">Status</label>
          <Select value={t.status} onValueChange={(v) => patch({ status: v })}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent>{STATUSES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent></Select>
        </div>
        <div>
          <label className="text-xs uppercase text-zinc-500">Priority</label>
          <Select value={t.priority} onValueChange={(v) => patch({ priority: v })}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent>{["low", "medium", "high", "urgent"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
        </div>
      </div>
      <div>
        <label className="text-xs uppercase text-zinc-500" htmlFor="task-detail-due">Due date (optional)</label>
        <Input id="task-detail-due" type="date" value={t.due_date || ""}
          data-testid="task-detail-due"
          onChange={(e) => setData({ ...data, task: { ...t, due_date: e.target.value } })}
          onBlur={(e) => patch({ due_date: e.target.value || null })}/>
        <div className="text-[11px] text-zinc-500 mt-1">
          When should this task be completed?
          {t.due_date && <span className="ml-1">· <b>Due {formatDueDate(t.due_date)}</b></span>}
        </div>
      </div>

      <div>
        <label className="text-xs uppercase text-zinc-500">Assignees</label>
        <div className="flex flex-wrap gap-1 mt-1">
          {members.map((m) => (
            <button key={m.user_id} onClick={() => patch({ assignee_ids: (t.assignee_ids || []).includes(m.user_id) ? t.assignee_ids.filter((x) => x !== m.user_id) : [...(t.assignee_ids || []), m.user_id] })}
              className={`text-xs rounded-full px-2 py-1 border ${(t.assignee_ids || []).includes(m.user_id) ? "border-[var(--brand-accent)] bg-orange-50" : "border-zinc-200"}`}>{m.user?.name}</button>
          ))}
        </div>
      </div>

      <div>
        <div className="text-xs uppercase text-zinc-500 mb-2">Comments</div>
        {(data.comments || []).map((c) => (
          <div key={c.id} className="flex gap-2 mb-2">
            <img src={renderUserAvatar(c.user)} className="w-6 h-6 rounded-full object-cover" alt=""/>
            <div>
              <div className="text-xs text-zinc-500">{c.user?.name}</div>
              <div className="text-sm">{c.text}</div>
            </div>
          </div>
        ))}
        <div className="flex gap-2">
          <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Add comment…"/>
          <Button onClick={addComment} size="sm">Send</Button>
        </div>
      </div>

      <div>
        <div className="text-xs uppercase text-zinc-500 mb-2">Activity</div>
        <div className="space-y-1 text-xs text-zinc-600">
          {(data.activity || []).map((a) => (
            <div key={a.id}>{a.actor?.name} <span className="text-zinc-400">{a.action}</span> · {new Date(a.created_at).toLocaleString()}</div>
          ))}
        </div>
      </div>
    </div>
  );
}
